import { forwardRef, type FormEvent, useState, useEffect } from 'react'
import {
  AiSearchInput as AiSearch,
  type AiSearchInputRef,
} from './ai-search-input/ai-search-input'
import { getDefaultSearchPlaceholders } from '../constants/default-search-placeholders'
import { queryAiSearch } from '../services/query-ai-search'
import {
  AI_SEARCH_FOCUS_EVENT_STORAGE_KEY,
  aiSearchInputFocusTrackingEvent,
  aiSearchInputSubmitTrackingEvent,
} from '../tracking'
import { useAnalytics } from '@cinch-nx/shared-analytics'

type AiSearchInputProps = {
  placeholders?: string[]
  showSubmitButton?: boolean
  onFocus?: (event: FormEvent<HTMLInputElement>) => void
}

export const AiSearchInput = forwardRef<AiSearchInputRef, AiSearchInputProps>(
  (props: AiSearchInputProps, ref) => {
    const { trackEvent } = useAnalytics()
    const [hasFocusTracked, setHasFocusTracked] = useState(false)

    useEffect(() => {
      setHasFocusTracked(
        sessionStorage.getItem(AI_SEARCH_FOCUS_EVENT_STORAGE_KEY) === 'true',
      )
    }, [])

    const onSearchHandler = async (message: string) => {
      const result = await queryAiSearch(message)
      if (result) {
        const event = aiSearchInputSubmitTrackingEvent(message, result)
        trackEvent(event)
      }
      const fallback = '/used-cars'
      if (result?.content?.searchUrl) {
        const resolvedUrl = new URL(
          result.content.searchUrl,
          window.location.origin,
        )
        // strip cross-origin redirects — keep only path + search + hash
        window.location.href =
          resolvedUrl.pathname + resolvedUrl.search + resolvedUrl.hash
      } else {
        window.location.href = fallback
      }
    }

    const onFocusCallback = (event: FormEvent<HTMLInputElement>) => {
      if (!hasFocusTracked) {
        trackEvent(aiSearchInputFocusTrackingEvent)
        sessionStorage.setItem(AI_SEARCH_FOCUS_EVENT_STORAGE_KEY, 'true')
        setHasFocusTracked(true)
      }
      props.onFocus?.(event)
    }
    return (
      <AiSearch
        ref={ref}
        aria-label={'AI powered search input'}
        placeholders={props.placeholders ?? getDefaultSearchPlaceholders()}
        showSubmitButton={props.showSubmitButton}
        onFocus={onFocusCallback}
        onSearch={onSearchHandler}
      />
    )
  },
)

AiSearchInput.displayName = 'AiSearchInput'
